import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classes from './BillingHistory.module.scss';
import invoiceClasses from '../Invoice/Invoice.module.scss';
import { SquareButtonIconsEnum } from '../../../constants/square-button-icons';
import { CenteredContentLayout } from 'components/containers/CenteredContentLayout';
import { Badge } from '../../../components/containers/Badge';
import { Invoice, InvoiceRenewalType, InvoiceStatus, PlanType } from '../../../models/graphql';
import { BadgeSizesEnum } from '../../../constants/badge';
import { Panel } from '../../../components/containers/Panel';
import { toTitleCase } from '../../../helpers/textHelpers';
import { getBadgeColor, getPaymentMethod, handleDownloadPDF } from '../../../helpers/billingHelpers';
import { DateStyle, useDateString } from '../../../hooks/useDateString';
import { TableWithActionColumn } from '../../../components/containers/TableWithActionColumn';
import { IconsEnum } from '../../../constants/icons';
import { InvoiceDetailView } from '../Invoice/InvoiceDetail.view';
import classNames from 'classnames';
import { EmptyPlaceholder } from '../../../components/containers/EmptyPlaceholder';
import noInvoicesBox from '../../../assets/images/no-invoices.svg';
import { BadgeViewProps } from '../../../components/containers/Badge/Badge.view';

export type BillingHistoryViewProps = {
  loading: boolean;
  invoices: Invoice[];
};

const getBadgeElement = (status: InvoiceStatus, invoiceId: string, failureMessage?: string) => {
  const badgeProps: BadgeViewProps = {
    color: getBadgeColor(status),
    size: BadgeSizesEnum.LARGE,
    text: String(InvoiceStatus[status]),
    ...(status === InvoiceStatus.FAILED
      ? {
          icon: IconsEnum.info,
          side: 'right',
          tooltipText: failureMessage ?? 'There was an unknown error',
          badgeKey: invoiceId
        }
      : {
          icon: undefined,
          tooltipText: undefined
        })
  };

  return <Badge {...badgeProps} />;
};
export const BillingHistoryView = ({ invoices, loading }: BillingHistoryViewProps): JSX.Element => {
  const { formatDateString } = useDateString();
  const navigate = useNavigate();
  const invoiceRef = useRef<HTMLDivElement | null>(null);

  const [invoiceIdToDownload, setInvoiceIdToDownload] = useState<string | null>(null);

  const selectedInvoice = useMemo(
    () => invoices.find(i => i.id === invoiceIdToDownload) ?? null,
    [invoiceIdToDownload, invoices]
  );

  const tableRows = useMemo(() => {
    return invoices.map(invoice => ({
      columns: [
        { header: 'ID', label: invoice.invoiceNumber },
        { header: 'Invoice Date', label: formatDateString(invoice.date, DateStyle.DATE_ONLY_SHORT_MONTH) },
        { header: 'Amount', label: `${invoice.amount} ${invoice.currencyCode}` },
        {
          header: 'Status',
          label: invoice.status ? (
            <div className={classes.StatusBadge}>
              {getBadgeElement(invoice.status, invoice.id, invoice.failureMessage || undefined)}
            </div>
          ) : (
            '' // what do we do if there is no status?
          )
        },
        { header: 'Plan', label: invoice.planType ? toTitleCase(PlanType[invoice.planType]) : '' },
        {
          header: 'Payment',
          label: invoice.paymentMethod ? getPaymentMethod(invoice.paymentMethod, invoice.creditCard || undefined) : ''
        },
        { header: 'Renewal', label: invoice.renewalType ? toTitleCase(InvoiceRenewalType[invoice.renewalType]) : '' }
      ],
      actions: [
        {
          action: () => navigate(`/settings/invoice/${invoice.id}`),
          icon: SquareButtonIconsEnum.show,
          disabled: invoice.status === InvoiceStatus.FAILED
        },
        {
          action: () => {
            Promise.all([setInvoiceIdToDownload(invoice.id)]).finally(() => {
              handleDownloadPDF(invoiceRef, invoice.id).finally(() => setInvoiceIdToDownload(null));
            });
          },
          icon: SquareButtonIconsEnum.download,
          disabled: invoice.status === InvoiceStatus.FAILED
        }
      ]
    }));
  }, [invoices, navigate, setInvoiceIdToDownload, formatDateString]);

  const printableInvoiceClasses = classNames(invoiceClasses.Invoice, invoiceClasses.PrintableInvoice);

  return (
    <CenteredContentLayout headerTitle={'Billing'} loading={loading} hasSidebar={true}>
      <div className={classes.BillingHistory}>
        {invoices.length > 0 ? (
          <Panel customClass={classes.InvoiceTablePanel}>
            <TableWithActionColumn rows={tableRows} />
          </Panel>
        ) : (
          <EmptyPlaceholder title={'No Invoices Found'} image={noInvoicesBox}>
            <div>After your first payment, you&apos;ll be able to view and download invoices for each payment.</div>
          </EmptyPlaceholder>
        )}
      </div>
      {selectedInvoice && (
        <div ref={invoiceRef} style={{ width: '800px' }}>
          <Panel customClass={printableInvoiceClasses}>
            <InvoiceDetailView invoice={selectedInvoice} />
          </Panel>
        </div>
      )}
    </CenteredContentLayout>
  );
};
